import { request } from '@/lib/api'

/**
 * Обёртки над `/api/v1/sales/plans/*` (#119).
 *
 * Сервер отдаёт JSON с `non_null`: процента нет в ответе вовсе, когда план нулевой
 * («цели не ставили»), поэтому он объявлен `?`, а не `| null`, и сравнивается нестрого.
 * Деньги приходят числом в валюте плана.
 *
 * Имена полей сверены с настоящим JSON серверным тестом
 * (`SalesPlanBoardIntegrationTest`): тест экрана на подставном ответе переименования не
 * заметил бы.
 */

export const RESOURCE = 'sales.plan'
export const PLANS_KEY = ['sales-plans']

/** Строка менеджера: план за его период и выигранное в нём. */
export interface EmployeeProgress {
  planId: string
  employeeId: string
  employeeName?: string
  periodStart: string
  periodEnd: string
  planned: number
  actual: number
  /** Нет при нулевом плане: «цели не ставили» — не то же, что «ничего не продал». */
  completionPercent?: number
  currency: string
}

/** Строка подразделения: факт — выигранные сделки отдела в периоде плана. */
export interface DepartmentProgress {
  planId: string
  departmentId: string
  departmentName?: string
  periodStart: string
  periodEnd: string
  planned: number
  actual: number
  completionPercent?: number
  currency: string
}

/** Прогноз: заработанное и взвешенная воронка — отдельно, а не одной цифрой. */
export interface Forecast {
  planId: string
  planned: number
  actual: number
  weightedPipeline: number
  projected: number
  projectedPercent?: number
  onTrack: boolean
}

/** Месяц периода: доля плана по календарным дням и факт. */
export interface MonthSlice {
  /** `YYYY-MM`. */
  month: string
  planned: number
  actual?: number
  cumulativeActual: number
  completionPercent?: number
}

export interface Assignee {
  id: string
  name: string
  departmentId?: string
  departmentName?: string
}

export interface Unit {
  id: string
  name: string
}

/** Кому вызывающий вправе задать план — по той же области, что проверяет заведение. */
export interface Assignees {
  employees: Assignee[]
  departments: Unit[]
  companyWide: boolean
}

/** Тело заведения: ровно одно измерение, границы периода, сумма. */
export interface PlanInput {
  employeeId?: string
  departmentId?: string
  periodStart: string
  periodEnd: string
  amount: number
  currency: string
}

export interface PlanView {
  id: string
  dimension: string
  employeeId?: string
  departmentId?: string
  periodStart: string
  periodEnd: string
  plannedAmount: number
  currency: string
}

const period = (from: string, to: string) =>
  `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`

export function fetchEmployeeProgress(from: string, to: string, signal?: AbortSignal) {
  return request<EmployeeProgress[]>(`/sales/plans/employee-progress?${period(from, to)}`, { signal })
}

export function fetchDepartmentProgress(from: string, to: string, signal?: AbortSignal) {
  return request<DepartmentProgress[]>(`/sales/plans/department-progress?${period(from, to)}`, { signal })
}

export function fetchForecast(planId: string, signal?: AbortSignal) {
  return request<Forecast>(`/sales/plans/${planId}/forecast`, { signal })
}

export function fetchBreakdown(planId: string, signal?: AbortSignal) {
  return request<MonthSlice[]>(`/sales/plans/${planId}/breakdown`, { signal })
}

export function fetchAssignees(signal?: AbortSignal) {
  return request<Assignees>('/sales/plans/assignees', { signal })
}

export function createPlan(input: PlanInput) {
  return request<PlanView>('/sales/plans', { method: 'POST', body: input })
}

export function revisePlan(planId: string, amount: number) {
  return request<PlanView>(`/sales/plans/${planId}/revise`, { method: 'POST', body: { amount } })
}

// ------------------------------------------------------------------ квартал

export interface Quarter {
  year: number
  /** 1–4. */
  q: number
}

export function quarterOf(date: Date): Quarter {
  return { year: date.getFullYear(), q: Math.floor(date.getMonth() / 3) + 1 }
}

export function shiftQuarter(quarter: Quarter, by: number): Quarter {
  const index = quarter.year * 4 + (quarter.q - 1) + by
  return { year: Math.floor(index / 4), q: (index % 4) + 1 }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Границы квартала датами `YYYY-MM-DD`, без часовых поясов: это календарь, а не момент. */
export function quarterRange(quarter: Quarter): { from: string; to: string } {
  const firstMonth = (quarter.q - 1) * 3 + 1
  const lastMonth = firstMonth + 2
  const lastDay = new Date(quarter.year, lastMonth, 0).getDate()
  return {
    from: `${quarter.year}-${pad(firstMonth)}-01`,
    to: `${quarter.year}-${pad(lastMonth)}-${pad(lastDay)}`,
  }
}

/**
 * Сумма, набранная человеком: «3 000 000,50» → 3000000.5.
 *
 * Пробелы (в том числе неразрывные) и запятая допустимы — так пишут суммы по-русски.
 * Всё остальное — null, а не ноль: план, молча записанный нулём, выглядит заданным.
 */
export function parseAmount(text: string): number | null {
  const normalized = text.replace(/[\s  ]/g, '').replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null
  return Number(normalized)
}
